import { arrayMove } from '@dnd-kit/sortable';
import { createContext } from 'react';
import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { generateId } from '@/utils';

export type MappingItem = {
  id?: string;
  from?: string;
  to?: string;
};

export interface MappingFormatterProps {
  formatterId: string;
  mapping: MappingItem[];
  hasChanges?: boolean;
}

export interface MappingFormatterState extends MappingFormatterProps {
  setMapping: (mapping: MappingItem[]) => void;
  addRow: (item: MappingItem) => void;
  updateRow: (id: string, updates: Partial<MappingItem>) => void;
  deleteRow: (id: string) => void;
  moveRow: (fromIndex: number, toIndex: number) => void;
}

export const createMappingFormatterStore = (formatterId: string, initProps?: Partial<MappingFormatterProps>) => {
  const defaultProps = {
    formatterId,
    hasChanges: false,
  };
  const mapping: MappingItem[] = (initProps?.mapping ?? []).map((item) => {
    return { id: generateId(), ...item };
  });
  const findRow = (mapping: MappingItem[], id: string) => {
    return mapping.find((item) => item.id === id);
  };
  return createStore<MappingFormatterState>()(
    devtools(
      immer((set, get) => ({
        ...defaultProps,
        ...initProps,
        ...{ mapping },
        setMapping: (mapping: MappingItem[]) =>
          set((state) => {
            state.mapping = mapping;
            state.hasChanges = true;
          }),
        addRow: (item: MappingItem) =>
          set((state) => {
            state.mapping.push(item);
            state.hasChanges = true;
          }),
        updateRow: (id: string, updates: Partial<MappingItem>) =>
          set((state) => {
            const row = findRow(state.mapping, id);
            if (row) {
              Object.assign(row, updates);
              state.hasChanges = true;
            }
          }),
        deleteRow: (id: string) =>
          set((state) => {
            state.mapping = state.mapping.filter((item) => item.id !== id);
            state.hasChanges = true;
          }),
        moveRow: (fromIndex: number, toIndex: number) =>
          set((state) => {
            const mapping = state.mapping;

            if (fromIndex < 0 || toIndex < 0) return;
            if (fromIndex >= mapping.length || toIndex >= mapping.length) return;

            state.mapping = arrayMove(state.mapping, fromIndex, toIndex);
            state.hasChanges = true;
          }),
      }))
    )
  );
};

export type MappingFormatterStore = ReturnType<typeof createMappingFormatterStore>;

export const MappingFormatterContext = createContext<MappingFormatterStore | null>(null);
